import * as React from "react";

import { cn, cva, type VariantProps } from "~/lib/utils";

/**
 * P0-11 · Button. Consumes the P0-DB01 button-knob tokens (--btn-radius /
 * --btn-weight / --btn-uppercase / --btn-tracking), so a brand can go
 * pill/uppercase vs square/sentence-case with no variant changes.
 */
export const buttonVariants = cva({
  base: cn(
    "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-body transition-[background-color,color,filter]",
    "rounded-[var(--btn-radius)] tracking-[var(--btn-tracking)] [font-weight:var(--btn-weight)] [text-transform:var(--btn-uppercase)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
    "disabled:pointer-events-none disabled:opacity-50",
  ),
  variants: {
    variant: {
      primary: "bg-primary text-primary-fg hover:brightness-95",
      secondary: "bg-secondary text-secondary-fg hover:brightness-95",
      outline: "border border-border bg-surface text-fg hover:bg-surface-2",
      ghost: "bg-transparent text-fg hover:bg-surface-2",
      destructive: "bg-danger text-danger-fg hover:brightness-95",
      link: "bg-transparent text-primary underline-offset-4 hover:underline",
    },
    size: {
      sm: "h-8 px-3 text-sm",
      md: "h-10 px-4 text-sm",
      lg: "h-11 px-6 text-base",
      icon: "size-10 p-0",
    },
  },
  defaultVariants: { variant: "primary", size: "md" },
});

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & { loading?: boolean };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, loading, disabled, children, ...props },
    ref,
  ) => (
    <button
      ref={ref}
      className={buttonVariants({ variant, size, className })}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          aria-hidden
          className="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      )}
      {children}
    </button>
  ),
);
Button.displayName = "Button";
